import { useRef, useCallback, useState, useEffect } from "react";
import * as THREE from "three";
import { anatomyConstants } from "../../constants/anatomyConstants";

const iconBase = "drop-shadow-[0_1px_2px_rgba(0,0,0,0.25)]";

const MSIcon = ({ name, className = "" }) => (
  <span
    className={`material-symbols-rounded select-none bg-gradient-to-br from-[var(--main-color)] to-[var(--main-color-bold)] text-transparent bg-clip-text ${className}`}
    aria-hidden
    style={{
      fontVariationSettings: '"FILL" 1, "wght" 650, "GRAD" 0, "opsz" 24',
      lineHeight: 1,
      display: "inline-block",
      fontSize: "32px",
      width: "32px",
      height: "32px",
    }}
  >
    {name}
  </span>
);

const Icon = {
  ArrowUp: () => (
    <span
      className={`material-symbols-rounded select-none bg-gradient-to-br from-[var(--main-color)] to-[var(--main-color-bold)] text-transparent bg-clip-text ${iconBase}`}
      aria-hidden
      style={{
        fontVariationSettings: '"FILL" 1, "wght" 650, "GRAD" 0, "opsz" 24',
        lineHeight: 1,
        display: "inline-block",
        transform: "translateX(-2px)",
        fontSize: "32px",
        width: "32px",
        height: "32px",
      }}
    >
      keyboard_arrow_up
    </span>
  ),
  ArrowDown: () => <MSIcon name="keyboard_arrow_down" className={iconBase} />,
  ArrowLeft: () => <MSIcon name="keyboard_arrow_left" className={iconBase} />,
  ArrowRight: () => <MSIcon name="keyboard_arrow_right" className={iconBase} />,
  ZoomIn: () => <MSIcon name="zoom_in" className={iconBase} />,
  ZoomOut: () => <MSIcon name="zoom_out" className={iconBase} />,
  Target: () => <MSIcon name="my_location" className={iconBase} />,
  RotateYawLeft: () => <MSIcon name="rotate_left" className={iconBase} />,
  RotateYawRight: () => <MSIcon name="rotate_right" className={iconBase} />,
  RotatePitchDown: () => (
    <MSIcon
      name="rotate_right"
      className={`${iconBase} rotate-90 scale-x-[-1] inline-block`}
    />
  ),
  RotatePitchUp: () => (
    <MSIcon
      name="rotate_left"
      className={`${iconBase} -rotate-90 scale-y-[-1] inline-block`}
    />
  ),

  RotateRollCW: () => (
    <MSIcon name="rotate_90_degrees_cw" className={iconBase} />
  ),
  RotateRollCCW: () => (
    <MSIcon name="rotate_90_degrees_ccw" className={iconBase} />
  ),
};

function useRepeater() {
  const rafId = useRef(null);
  const running = useRef(false);
  const last = useRef(0);

  const stop = useCallback(() => {
    if (rafId.current) cancelAnimationFrame(rafId.current);
    rafId.current = null;
    running.current = false;
  }, []);

  const start = useCallback(
    (fn) => {
      if (running.current) stop();
      running.current = true;
      const loop = (now) => {
        if (!running.current) return;
        const prev = last.current || now;
        const dt = Math.min(0.05, (now - prev) / 1000);
        last.current = now;
        fn(dt);
        rafId.current = requestAnimationFrame(loop);
      };
      last.current = 0;
      rafId.current = requestAnimationFrame(loop);
    },
    [stop]
  );

  return { start, stop };
}

const EPS = 1e-9;

function getCtx(controlsRef, cameraRef) {
  const controls = controlsRef?.current || null;
  const camera = cameraRef?.current || null;
  if (!controls || !camera) return null;
  return { controls, camera };
}

function getLocalFrame(camera, target) {
  const viewDir = target.clone().sub(camera.position);
  const viewLen = viewDir.length();
  if (viewLen < EPS) {
    return {
      viewDir: new THREE.Vector3(0, 0, -1),
      up: new THREE.Vector3(0, 1, 0),
      right: new THREE.Vector3(1, 0, 0),
      dist: 1,
    };
  }
  const v = viewDir.clone().divideScalar(viewLen);
  let up = camera.up.clone();
  if (Math.abs(up.dot(v)) > 0.999) up = new THREE.Vector3(0, 1, 0);
  up = up.addScaledVector(v, -up.dot(v)).normalize();
  const right = new THREE.Vector3().crossVectors(v, up).normalize();
  return { viewDir: v, up, right, dist: viewLen };
}

function updateView(camera, controls, target) {
  camera.lookAt(target);
  camera.updateProjectionMatrix?.();
  controls?.update?.();
}

function rotateYaw(controlsRef, cameraRef, radiansPerSec, dt) {
  const ctx = getCtx(controlsRef, cameraRef);
  if (!ctx) return;
  const { camera, controls } = ctx;
  if (controls.enabled === false || controls.noRotate) return;

  const target = controls.target.clone();
  const { up } = getLocalFrame(camera, target);
  const q = new THREE.Quaternion().setFromAxisAngle(up, radiansPerSec * dt);
  const offset = camera.position.clone().sub(target).applyQuaternion(q);
  camera.position.copy(target.clone().add(offset));
  updateView(camera, controls, target);
}

function rotatePitch(controlsRef, cameraRef, radiansPerSec, dt, clamp = true) {
  const ctx = getCtx(controlsRef, cameraRef);
  if (!ctx) return;
  const { camera, controls } = ctx;
  if (controls.enabled === false || controls.noRotate) return;

  const target = controls.target.clone();
  const { viewDir, up, right } = getLocalFrame(camera, target);

  let angle = radiansPerSec * dt;
  if (clamp) {
    const cosAngleToUp = Math.abs(viewDir.dot(up));
    if (cosAngleToUp > 0.99) angle *= 0.2;
    else if (cosAngleToUp > 0.97) angle *= 0.5;
  }

  const q = new THREE.Quaternion().setFromAxisAngle(right, angle);
  const offset = camera.position.clone().sub(target).applyQuaternion(q);
  camera.position.copy(target.clone().add(offset));
  camera.up.copy(camera.up.clone().applyQuaternion(q)).normalize();

  updateView(camera, controls, target);
}

function panBy(controlsRef, cameraRef, pxPerSecX, pxPerSecY, dt) {
  const ctx = getCtx(controlsRef, cameraRef);
  if (!ctx) return;
  const { camera, controls } = ctx;
  if (controls.enabled === false || controls.noPan) return;

  const el = controls.domElement || camera.domElement || document.body;
  const vw = el.clientWidth || 1;
  const vh = el.clientHeight || 1;

  const target = controls.target.clone();
  const { up, right, dist } = getLocalFrame(camera, target);

  const fovY = THREE.MathUtils.degToRad(camera.fov);
  const worldPerPixelY = (2 * dist * Math.tan(fovY / 2)) / vh;
  const worldPerPixelX = worldPerPixelY * (vw / vh);

  const moveX = pxPerSecX * dt * worldPerPixelX;
  const moveY = pxPerSecY * dt * worldPerPixelY;

  const delta = right.multiplyScalar(moveX).add(up.multiplyScalar(-moveY));
  camera.position.add(delta);
  controls.target.add(delta);

  updateView(camera, controls, controls.target);
}

function zoom(controlsRef, cameraRef, unitsPerSec, dt) {
  const ctx = getCtx(controlsRef, cameraRef);
  if (!ctx) return;
  const { camera, controls } = ctx;
  if (controls.enabled === false || controls.noZoom) return;

  const target = controls.target.clone();
  const offset = camera.position.clone().sub(target);
  const len = offset.length();
  if (len < EPS) return;

  const factor = Math.exp(unitsPerSec * dt);
  let next = len / factor;

  const minD = Number.isFinite(controls.minDistance) ? controls.minDistance : 0;
  const maxD = Number.isFinite(controls.maxDistance)
    ? controls.maxDistance
    : Infinity;
  next = THREE.MathUtils.clamp(next, minD, maxD);

  offset.setLength(next);
  camera.position.copy(target.clone().add(offset));

  updateView(camera, controls, target);
}

function rotateRoll(controlsRef, cameraRef, radiansPerSec, dt) {
  const ctx = getCtx(controlsRef, cameraRef);
  if (!ctx) return;
  const { camera, controls } = ctx;
  if (controls.enabled === false || controls.noRotate) return;

  const target = controls.target.clone();
  const viewDir = target.clone().sub(camera.position);
  const len = viewDir.length();
  if (len < EPS) return;

  const axis = viewDir.normalize();
  const q = new THREE.Quaternion().setFromAxisAngle(axis, radiansPerSec * dt);

  const offset = camera.position.clone().sub(target).applyQuaternion(q);
  camera.position.copy(target.clone().add(offset));
  camera.up.copy(camera.up.clone().applyQuaternion(q)).normalize();

  updateView(camera, controls, target);
}

export default function OnScreenNav({
  controlsRef,
  cameraRef,
  rotationSpeed = Math.PI * 0.2,
  panSpeedPxPerSec = 200,
  zoomUnitsPerSec = 2.2,
  onReset,
  isSidebarOpen = false,
  className = "",
}) {
  const repeat = useRepeater();
  const [fontsLoaded, setFontsLoaded] = useState(false);

  useEffect(() => {
    // Check if fonts are already loaded
    if (document.fonts) {
      document.fonts.ready.then(() => {
        setFontsLoaded(true);
      });
      // Also check immediately in case fonts are already loaded
      if (document.fonts.check("1em Material Symbols Rounded")) {
        setFontsLoaded(true);
      }
    } else {
      // Fallback for browsers without Font Loading API
      setFontsLoaded(true);
    }
  }, []);

  const reset = () => {
    if (typeof onReset === "function") return onReset();
    const ctx = getCtx(controlsRef, cameraRef);
    if (!ctx) return;
    const { camera, controls } = ctx;
    const t = controls.target.clone();
    camera.lookAt(t);
    camera.updateProjectionMatrix?.();
    controls.update();
  };

  const hold = (fn) => ({
    onPointerDown: (e) => {
      e.stopPropagation();
      repeat.start(fn);
    },
    onPointerUp: (e) => {
      e.stopPropagation();
      repeat.stop();
    },
    onPointerLeave: repeat.stop,
  });

  const btnBase =
    "inline-flex items-center justify-center rounded-full shrink-0 pointer-events-auto " +
    "text-[var(--main-color)] select-none " +
    "hover:opacity-90 active:scale-95 transition " +
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40";
  const btn = `${btnBase} w-8 h-8`;
  const zoomBtn = `${btnBase} w-10 h-10`;

  const aspectSquare = { aspectRatio: "1 / 1" };

  return (
    <div
      className={`absolute top-0 ${isSidebarOpen ? 'right-80' : 'right-0'} z-50 pointer-events-none transition-all duration-300 ${className}`}
      data-onscreen-nav
      onPointerDown={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
      style={{
        "--main-color": anatomyConstants.mainColor,
        "--main-color-bold": anatomyConstants.mainColorBold,
        opacity: fontsLoaded ? 1 : 0,
        transition: "opacity 0.3s ease-in",
      }}
    >
      <div
        className="grid grid-cols-5 grid-rows-5 gap-3 p-4"
        style={{ width: 240, height: 240 }}
      >
        <div />
        <div />
        <div className="flex items-center justify-center">
          <button
            className={btn}
            style={aspectSquare}
            title="Pan up"
            {...hold((dt) =>
              panBy(controlsRef, cameraRef, 0, +panSpeedPxPerSec, dt)
            )}
          >
            <Icon.ArrowUp />
          </button>
        </div>
        <div />
        <div />

        <div />
        <div className="flex items-center justify-center">
          <button
            className={zoomBtn}
            style={aspectSquare}
            title="Zoom out"
            {...hold((dt) =>
              zoom(controlsRef, cameraRef, -zoomUnitsPerSec, dt)
            )}
          >
            <Icon.ZoomOut />
          </button>
        </div>
        <div className="flex items-center justify-center">
          <button
            className={btn}
            style={aspectSquare}
            title="Rotate up"
            {...hold((dt) =>
              rotatePitch(controlsRef, cameraRef, +rotationSpeed, dt)
            )}
          >
            <Icon.RotatePitchDown />
          </button>
        </div>
        <div className="flex items-center justify-center">
          <button
            className={zoomBtn}
            style={aspectSquare}
            title="Zoom in"
            {...hold((dt) =>
              zoom(controlsRef, cameraRef, +zoomUnitsPerSec, dt)
            )}
          >
            <Icon.ZoomIn />
          </button>
        </div>
        <div />

        <div className="flex items-center justify-center">
          <button
            className={btn}
            style={aspectSquare}
            title="Pan left"
            {...hold((dt) =>
              panBy(controlsRef, cameraRef, +panSpeedPxPerSec, 0, dt)
            )}
          >
            <Icon.ArrowLeft />
          </button>
        </div>
        <div className="flex items-center justify-center">
          <button
            className={btn}
            style={aspectSquare}
            title="Rotate left"
            {...hold((dt) =>
              rotateYaw(controlsRef, cameraRef, +rotationSpeed, dt)
            )}
          >
            <Icon.RotateYawRight />
          </button>
        </div>
        <div className="flex items-center justify-center">
          <button
            className={btn}
            style={aspectSquare}
            title="Reset / recenter"
            onClick={reset}
          >
            <Icon.Target />
          </button>
        </div>
        <div className="flex items-center justify-center">
          <button
            className={btn}
            style={aspectSquare}
            title="Rotate right"
            {...hold((dt) =>
              rotateYaw(controlsRef, cameraRef, -rotationSpeed, dt)
            )}
          >
            <Icon.RotateYawLeft />
          </button>
        </div>
        <div className="flex items-center justify-center">
          <button
            className={btn}
            style={aspectSquare}
            title="Pan right"
            {...hold((dt) =>
              panBy(controlsRef, cameraRef, -panSpeedPxPerSec, 0, dt)
            )}
          >
            <Icon.ArrowRight />
          </button>
        </div>

        <div />
        <div className="flex items-center justify-center">
          <button
            className={btn}
            style={aspectSquare}
            title="Roll counterclockwise"
            {...hold((dt) =>
              rotateRoll(controlsRef, cameraRef, +rotationSpeed, dt)
            )}
          >
            <Icon.RotateRollCCW />
          </button>
        </div>
        <div className="flex items-center justify-center">
          <button
            className={btn}
            style={aspectSquare}
            title="Rotate down"
            {...hold((dt) =>
              rotatePitch(controlsRef, cameraRef, -rotationSpeed, dt)
            )}
          >
            <Icon.RotatePitchUp />
          </button>
        </div>
        <div className="flex items-center justify-center">
          <button
            className={btn}
            style={aspectSquare}
            title="Roll clockwise"
            {...hold((dt) =>
              rotateRoll(controlsRef, cameraRef, -rotationSpeed, dt)
            )}
          >
            <Icon.RotateRollCW />
          </button>
        </div>
        <div />

        <div />
        <div />
        <div className="flex items-center justify-center">
          <button
            className={btn}
            style={aspectSquare}
            title="Pan down"
            {...hold((dt) =>
              panBy(controlsRef, cameraRef, 0, -panSpeedPxPerSec, dt)
            )}
          >
            <Icon.ArrowDown />
          </button>
        </div>
        <div />
        <div />
      </div>
    </div>
  );
}
